import { type DropDownMenuItemProps, DropdownMenu } from '@/Components/filterbar/components/DropdownMenu';
import { OrderEnum, PostObjectsConnectionOrderbyEnum, type PostObjectsConnectionOrderbyInput } from '@/graphql/types';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faArrowDown19, faArrowDown91, faArrowDownAZ, faArrowDownZA } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useMemo } from 'react';
import { z } from 'zod';
import { FilterBadge } from '../components/FilterBadge';
import { __useFilterState } from '../useFilterState';

const orderSearchSchema = z.object({
  order: z
    .object({
      field: z.enum([PostObjectsConnectionOrderbyEnum.Title, PostObjectsConnectionOrderbyEnum.Date]),
      order: z.enum([OrderEnum.Asc, OrderEnum.Desc]),
    })
    .optional()
    .nullable(),
});

const orderComponentProps = z.object({
  displayName: z.string().optional(),
  name: z.string().optional(),
});

type OrderFilterProps = z.infer<typeof orderComponentProps>;

const MENU_OPTIONS = [
  [
    'Title A-Z',
    <FontAwesomeIcon key={0} icon={faArrowDownAZ as IconProp} />,
    { field: PostObjectsConnectionOrderbyEnum.Title, order: OrderEnum.Asc },
  ],
  [
    'Title Z-A',
    <FontAwesomeIcon key={1} icon={faArrowDownZA as IconProp} />,
    { field: PostObjectsConnectionOrderbyEnum.Title, order: OrderEnum.Desc },
  ],
  [
    'Date ASC',
    <FontAwesomeIcon key={2} icon={faArrowDown19 as IconProp} />,
    { field: PostObjectsConnectionOrderbyEnum.Date, order: OrderEnum.Asc },
  ],
  [
    'Date DESC',
    <FontAwesomeIcon key={3} icon={faArrowDown91 as IconProp} />,
    { field: PostObjectsConnectionOrderbyEnum.Date, order: OrderEnum.Desc },
  ],
] as Array<[string, React.JSX.Element, PostObjectsConnectionOrderbyInput]>;

const OrderFilter = ({ displayName = 'Order', name }: OrderFilterProps) => {
  const { fastFilters, slowFilters, updateFilter, isUpdating } = __useFilterState(orderSearchSchema);

  const handleOrderSelect = useCallback(
    (orderInput: PostObjectsConnectionOrderbyInput) => {
      updateFilter((prev) => {
        const currentOrder = prev?.order;

        const isAlreadySelected = currentOrder?.field === orderInput.field && currentOrder?.order === orderInput.order;

        if (isAlreadySelected) {
          return {
            order: undefined,
          };
        }
        return {
          order: {
            field: orderInput.field as PostObjectsConnectionOrderbyEnum.Date | PostObjectsConnectionOrderbyEnum.Title,
            order: orderInput.order,
          },
        };
      });
    },
    [updateFilter]
  );

  const menuItems = useMemo(() => {
    return MENU_OPTIONS.map(([name, icon, orderInput]) => ({
      name,
      icon,
      value: orderInput,
      onClick: () => {
        handleOrderSelect(orderInput);
      },
    })) as DropDownMenuItemProps[];
  }, [handleOrderSelect]);

  return (
    <div style={{ position: 'relative' }}>
      <DropdownMenu
        className="z-btn-6"
        name={displayName || name || 'Order'}
        menuList={menuItems}
        type="single-select"
        options={{
          filterBar: slowFilters,
          filterBarKey: 'order',
          filterBarComparison(itemState, filterParam) {
            try {
              return JSON.stringify(itemState) === JSON.stringify(filterParam);
            } catch (error) {
              return false;
            }
          },
          isSearchable: false,
        }}
      />
      <FilterBadge count={!!fastFilters?.order} isUpdating={isUpdating} />
    </div>
  );
};

const OrderFilterMemo = React.memo(OrderFilter);

export const FilterConfig_Order = {
  order: {
    searchSchema: orderSearchSchema,
    component: OrderFilterMemo,
    componentProps: orderComponentProps,
  },
} as const;
